import pkg from "../package.json" with { type: "json" };
import { normalizeChassisCode } from "./chassis-codes.ts";
import { normalizeEngineCode } from "./engine-codes.ts";
import { makerKey } from "./makers.ts";
import { CURRENCIES } from "./money.ts";
import { normalizePartNumber } from "./part-number.ts";
import { isUnitCode, type UnitCode } from "./units.ts";
import {
  isCountryCode,
  isLanguageCode,
  PART_NUMBER_KINDS,
  PART_ORIGINS,
  type PartNumberKind,
  type PartOrigin,
} from "./vocabularies.ts";

/**
 * The parts catalogue's snapshot: what the collector hands an installation (ADR 0015). A zip with
 * `manifest.json` and `records.jsonl`, one record per line: a catalogue part with its numbers,
 * texts, fits and offers, or an offer that is gone. It carries natural keys, never database ids,
 * and every time is ISO UTC.
 */

/** This library's version; it goes up when a rule or the data changes what something means. */
export const PARTS_CORE_VERSION: string = pkg.version;

export const CATALOGUE_KIND = "parts-catalogue";
export const CATALOGUE_RECEIPT_KIND = "parts-catalogue-receipt";

/** The snapshot format this library writes, and the oldest it still reads (through `upgradeRecord`). */
export const CATALOGUE_FORMAT = 1;
export const CATALOGUE_MIN_FORMAT = 1;

export const CATALOGUE_SOURCE_KINDS = ["shop", "garage"] as const;
export type CatalogueSourceKind = (typeof CATALOGUE_SOURCE_KINDS)[number];

/** A catalogue part's numbers: the app's kinds, and the item's EAN (barcode number). */
export const CATALOGUE_NUMBER_KINDS = [...PART_NUMBER_KINDS, "ean"] as const;
export type CatalogueNumberKind = (typeof CATALOGUE_NUMBER_KINDS)[number];

export const CATALOGUE_AVAILABILITIES = ["in_stock", "delayed", "unavailable", "unknown"] as const;
export type CatalogueAvailability = (typeof CATALOGUE_AVAILABILITIES)[number];

/** Where catalogue data comes from: its texts are in `language`, it sells to `country`. */
export interface CatalogueSource {
  id: string;
  name: string;
  website: string;
  kind: CatalogueSourceKind;
  country: string;
  language: string;
}

export interface SnapshotManifest {
  kind: typeof CATALOGUE_KIND;
  format: number;
  snapshotId: string;
  /** The snapshot this one builds on; null starts a chain (a full snapshot). */
  baseSnapshotId: string | null;
  createdAt: string;
  source: CatalogueSource;
  /** The library version the records were made with. */
  partsCore: string;
  counts: { parts: number; gone: number };
}

export interface CatalogueNumber {
  number: string;
  /** `normalizePartNumber(number)`. */
  normalized: string;
  kind: CatalogueNumberKind;
  brand: string | null;
}

export interface CatalogueText {
  language: string;
  name: string;
}

/** One chassis code a part fits, with the engines the source names for it. */
export interface CatalogueFitment {
  /** A normalized chassis code key (`normalizeChassisCode`); each once per part. */
  chassis: string;
  /** Normalized engine codes (`normalizeEngineCode`), each once; empty when the source names none. */
  engines: string[];
  /** How the source wrote it ("3er E90 LCI Limousine"). */
  label: string;
}

export interface CatalogueOffer {
  url: string;
  sku: string | null;
  /** The country the price was collected for. */
  country: string;
  /** Excl. tax, in hundredths of a minor unit (ADR 0009); null when the source showed none. */
  priceExclTax: number | null;
  currency: string;
  /** The rate the shown price was converted at, in basis points. */
  taxRateBp: number;
  availability: CatalogueAvailability;
  seenAt: string;
}

export interface CataloguePartRecord {
  type: "part";
  /** `cataloguePartKey` of the part's primary number. */
  key: string;
  origin: PartOrigin;
  maker: string;
  mainGroup: string | null;
  weightGrams: number | null;
  pack: { quantity: number; unit: UnitCode } | null;
  hazardous: boolean | null;
  numbers: CatalogueNumber[];
  /** Normalized genuine numbers this alternative replaces ("Kompatibel zu"). */
  replaces: string[];
  /** Normalized numbers the source says name the same item, kept as a claim of this source. */
  sameAs: string[];
  texts: CatalogueText[];
  fitments: CatalogueFitment[];
  offers: CatalogueOffer[];
}

/** An offer the source no longer lists. */
export interface CatalogueGoneRecord {
  type: "gone";
  url: string;
  country: string;
  goneAt: string;
}

export type CatalogueRecord = CataloguePartRecord | CatalogueGoneRecord;

/** What an installation's import leaves next to the snapshot it was given. */
export interface SnapshotReceipt {
  kind: typeof CATALOGUE_RECEIPT_KIND;
  snapshotId: string;
  installation: string;
  importedAt: string;
  counts: { parts: number; offers: number; gone: number };
  /** Chassis codes the installation doesn't know as built-in, normalized. */
  unknownChassisCodes: string[];
}

/**
 * The catalogue part's natural key: `genuine:<number>` for a genuine part, else
 * `<maker>:<number>` with the maker's own number ("nrf:79203700"), since makers' numbers can
 * repeat. The same number from another source lands on the same part.
 */
export function cataloguePartKey(part: {
  origin: PartOrigin;
  maker: string;
  number: string;
}): string {
  const number = normalizePartNumber(part.number);
  return part.origin === "genuine" ? `genuine:${number}` : `${makerKey(part.maker)}:${number}`;
}

/** The app's kind for a catalogue number: an EAN names the same item, so it is an alternative. */
export function appNumberKind(kind: CatalogueNumberKind): PartNumberKind {
  return kind === "ean" ? "alternative" : kind;
}

export type Checked<T> = { ok: true; value: T } | { ok: false; problems: string[] };

type Obj = Record<string, unknown>;

const ISO_UTC = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/;
const ID = /^[a-z0-9][a-z0-9-]{0,63}$/;

function isObj(value: unknown): value is Obj {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isText(value: unknown): value is string {
  return typeof value === "string" && value.length > 0 && value.trim() === value;
}

function isUtc(value: unknown): value is string {
  return typeof value === "string" && ISO_UTC.test(value) && !Number.isNaN(Date.parse(value));
}

function isCount(value: unknown): value is number {
  return Number.isInteger(value) && (value as number) >= 0;
}

/** An https URL with a host name and no spaces; the library has no `URL` (no DOM or Node types). */
const HTTPS = /^https:\/\/[a-z0-9-]+(\.[a-z0-9-]+)+(:\d{1,5})?([/?#][^\s]*)?$/i;

function isHttps(value: unknown): value is string {
  return typeof value === "string" && HTTPS.test(value);
}

function oneOf<T extends string>(ids: readonly T[], value: unknown): value is T {
  return typeof value === "string" && (ids as readonly string[]).includes(value);
}

/** Collects problems with a path, like reference-data.ts does. */
class Checker {
  readonly problems: string[] = [];
  need(ok: boolean, at: string, what: string): boolean {
    if (!ok) this.problems.push(`${at}: ${what}`);
    return ok;
  }
  done<T>(value: unknown): Checked<T> {
    return this.problems.length === 0
      ? { ok: true, value: value as T }
      : { ok: false, problems: this.problems };
  }
}

function checkSource(c: Checker, at: string, value: unknown): void {
  if (!c.need(isObj(value), at, "expected an object")) return;
  const source = value as Obj;
  c.need(typeof source.id === "string" && ID.test(source.id), `${at}.id`, "expected a short id");
  c.need(isText(source.name), `${at}.name`, "missing");
  c.need(isHttps(source.website), `${at}.website`, "expected an https URL");
  c.need(oneOf(CATALOGUE_SOURCE_KINDS, source.kind), `${at}.kind`, "expected shop or garage");
  c.need(isCountryCode(source.country), `${at}.country`, "expected an ISO 3166-1 code");
  c.need(isLanguageCode(source.language), `${at}.language`, "expected an ISO 639-1 code");
}

/** Checks a `manifest.json`, including that this library can read its format. */
export function validateManifest(value: unknown): Checked<SnapshotManifest> {
  const c = new Checker();
  if (!c.need(isObj(value), "manifest", "expected an object")) return c.done(value);
  const m = value as Obj;
  c.need(m.kind === CATALOGUE_KIND, "manifest.kind", `expected "${CATALOGUE_KIND}"`);
  c.need(
    Number.isInteger(m.format) &&
      (m.format as number) >= CATALOGUE_MIN_FORMAT &&
      (m.format as number) <= CATALOGUE_FORMAT,
    "manifest.format",
    `expected ${CATALOGUE_MIN_FORMAT} to ${CATALOGUE_FORMAT}`,
  );
  c.need(
    typeof m.snapshotId === "string" && ID.test(m.snapshotId),
    "manifest.snapshotId",
    "expected a short id",
  );
  c.need(
    m.baseSnapshotId === null ||
      (typeof m.baseSnapshotId === "string" && ID.test(m.baseSnapshotId)),
    "manifest.baseSnapshotId",
    "expected a short id or null",
  );
  c.need(m.baseSnapshotId !== m.snapshotId, "manifest.baseSnapshotId", "the snapshot itself");
  c.need(isUtc(m.createdAt), "manifest.createdAt", "expected an ISO UTC time");
  checkSource(c, "manifest.source", m.source);
  c.need(isText(m.partsCore), "manifest.partsCore", "missing");
  const counts = isObj(m.counts) ? m.counts : {};
  c.need(
    isCount(counts.parts) && isCount(counts.gone),
    "manifest.counts",
    "expected parts and gone",
  );
  return c.done(value);
}

function checkNumbers(c: Checker, at: string, value: unknown): void {
  if (!c.need(Array.isArray(value) && value.length > 0, at, "expected a non-empty list")) return;
  const list = value as unknown[];
  list.forEach((raw, i) => {
    const here = `${at}[${i}]`;
    if (!c.need(isObj(raw), here, "expected an object")) return;
    const n = raw as Obj;
    if (c.need(isText(n.number), `${here}.number`, "missing")) {
      c.need(
        n.normalized === normalizePartNumber(n.number as string) && n.normalized !== "",
        `${here}.normalized`,
        "not the normalized number",
      );
    }
    c.need(oneOf(CATALOGUE_NUMBER_KINDS, n.kind), `${here}.kind`, "unknown kind");
    c.need(n.brand === null || isText(n.brand), `${here}.brand`, "expected a text or null");
  });
  const primaries = list.filter((n) => isObj(n) && n.kind === "primary").length;
  c.need(primaries === 1, at, "expected exactly one primary number");
}

function checkNormalizedNumbers(c: Checker, at: string, value: unknown): void {
  if (!c.need(Array.isArray(value), at, "expected a list")) return;
  for (const [i, n] of (value as unknown[]).entries()) {
    c.need(
      typeof n === "string" && n !== "" && normalizePartNumber(n) === n,
      `${at}[${i}]`,
      "expected a normalized number",
    );
  }
}

function checkOffer(c: Checker, at: string, value: unknown): void {
  if (!c.need(isObj(value), at, "expected an object")) return;
  const o = value as Obj;
  c.need(isHttps(o.url), `${at}.url`, "expected an https URL");
  c.need(o.sku === null || isText(o.sku), `${at}.sku`, "expected a text or null");
  c.need(isCountryCode(o.country), `${at}.country`, "expected an ISO 3166-1 code");
  c.need(
    o.priceExclTax === null || (isCount(o.priceExclTax) && o.priceExclTax <= 2_000_000_000),
    `${at}.priceExclTax`,
    "expected a whole amount or null",
  );
  c.need(oneOf(CURRENCIES, o.currency), `${at}.currency`, "unknown currency");
  c.need(
    isCount(o.taxRateBp) && (o.taxRateBp as number) <= 10_000,
    `${at}.taxRateBp`,
    "expected basis points from 0 to 10000",
  );
  c.need(oneOf(CATALOGUE_AVAILABILITIES, o.availability), `${at}.availability`, "unknown");
  c.need(isUtc(o.seenAt), `${at}.seenAt`, "expected an ISO UTC time");
}

function checkPart(c: Checker, at: string, r: Obj): void {
  c.need(oneOf(PART_ORIGINS, r.origin), `${at}.origin`, "unknown origin");
  c.need(isText(r.maker), `${at}.maker`, "missing");
  checkNumbers(c, `${at}.numbers`, r.numbers);
  const primary = Array.isArray(r.numbers)
    ? (r.numbers as unknown[]).find((n): n is Obj => isObj(n) && n.kind === "primary")
    : undefined;
  if (primary && isText(primary.number) && oneOf(PART_ORIGINS, r.origin) && isText(r.maker)) {
    const key = cataloguePartKey({ origin: r.origin, maker: r.maker, number: primary.number });
    c.need(r.key === key, `${at}.key`, `expected "${key}"`);
  }
  c.need(r.mainGroup === null || isText(r.mainGroup), `${at}.mainGroup`, "expected a text or null");
  c.need(
    r.weightGrams === null || isCount(r.weightGrams),
    `${at}.weightGrams`,
    "expected whole grams or null",
  );
  if (r.pack !== null) {
    const pack = isObj(r.pack) ? r.pack : {};
    c.need(
      isCount(pack.quantity) && (pack.quantity as number) > 0 && isUnitCode(String(pack.unit)),
      `${at}.pack`,
      "expected a quantity in milli-units and a unit, or null",
    );
  }
  c.need(
    r.hazardous === null || typeof r.hazardous === "boolean",
    `${at}.hazardous`,
    "expected true, false or null",
  );
  checkNormalizedNumbers(c, `${at}.replaces`, r.replaces);
  checkNormalizedNumbers(c, `${at}.sameAs`, r.sameAs);
  if (
    c.need(Array.isArray(r.texts) && r.texts.length > 0, `${at}.texts`, "expected a non-empty list")
  ) {
    const languages = new Set<unknown>();
    (r.texts as unknown[]).forEach((raw, i) => {
      const t = isObj(raw) ? raw : {};
      c.need(
        isLanguageCode(t.language),
        `${at}.texts[${i}].language`,
        "expected an ISO 639-1 code",
      );
      c.need(!languages.has(t.language), `${at}.texts[${i}].language`, "twice");
      languages.add(t.language);
      c.need(isText(t.name), `${at}.texts[${i}].name`, "missing");
    });
  }
  if (c.need(Array.isArray(r.fitments), `${at}.fitments`, "expected a list")) {
    const chassis = new Set<unknown>();
    for (const [i, raw] of (r.fitments as unknown[]).entries()) {
      const f = isObj(raw) ? raw : {};
      const here = `${at}.fitments[${i}]`;
      c.need(
        typeof f.chassis === "string" &&
          f.chassis !== "" &&
          normalizeChassisCode(f.chassis) === f.chassis,
        `${here}.chassis`,
        "expected a normalized chassis code",
      );
      c.need(!chassis.has(f.chassis), `${here}.chassis`, "twice");
      chassis.add(f.chassis);
      const engines = Array.isArray(f.engines) ? (f.engines as unknown[]) : null;
      c.need(
        engines !== null &&
          new Set(engines).size === engines.length &&
          engines.every((e) => typeof e === "string" && e !== "" && normalizeEngineCode(e) === e),
        `${here}.engines`,
        "expected normalized engine codes, each once",
      );
      c.need(typeof f.label === "string", `${here}.label`, "expected a text");
    }
  }
  if (c.need(Array.isArray(r.offers), `${at}.offers`, "expected a list")) {
    for (const [i, o] of (r.offers as unknown[]).entries()) checkOffer(c, `${at}.offers[${i}]`, o);
  }
}

/** Checks one line of `records.jsonl`, already upgraded to this format (`upgradeRecord`). */
export function validateRecord(value: unknown, at = "record"): Checked<CatalogueRecord> {
  const c = new Checker();
  if (!c.need(isObj(value), at, "expected an object")) return c.done(value);
  const r = value as Obj;
  if (r.type === "part") checkPart(c, at, r);
  else if (r.type === "gone") {
    c.need(isHttps(r.url), `${at}.url`, "expected an https URL");
    c.need(isCountryCode(r.country), `${at}.country`, "expected an ISO 3166-1 code");
    c.need(isUtc(r.goneAt), `${at}.goneAt`, "expected an ISO UTC time");
  } else c.need(false, `${at}.type`, 'expected "part" or "gone"');
  return c.done(value);
}

/** Checks a receipt an import left next to a snapshot. */
export function validateReceipt(value: unknown): Checked<SnapshotReceipt> {
  const c = new Checker();
  if (!c.need(isObj(value), "receipt", "expected an object")) return c.done(value);
  const r = value as Obj;
  c.need(r.kind === CATALOGUE_RECEIPT_KIND, "receipt.kind", `expected "${CATALOGUE_RECEIPT_KIND}"`);
  c.need(
    typeof r.snapshotId === "string" && ID.test(r.snapshotId),
    "receipt.snapshotId",
    "expected a short id",
  );
  c.need(
    typeof r.installation === "string" && ID.test(r.installation),
    "receipt.installation",
    "expected a short id",
  );
  c.need(isUtc(r.importedAt), "receipt.importedAt", "expected an ISO UTC time");
  const counts = isObj(r.counts) ? r.counts : {};
  c.need(
    isCount(counts.parts) && isCount(counts.offers) && isCount(counts.gone),
    "receipt.counts",
    "expected parts, offers and gone",
  );
  c.need(
    Array.isArray(r.unknownChassisCodes) &&
      r.unknownChassisCodes.every((k) => typeof k === "string"),
    "receipt.unknownChassisCodes",
    "expected a list of codes",
  );
  return c.done(value);
}

/**
 * Upgrades a record from an older format, one step at a time; a format change adds its step
 * here and a fixture of the old format, which must keep importing.
 */
const UPGRADERS: Record<number, (record: Obj) => Obj> = {};

export function upgradeRecord(record: unknown, format: number): unknown {
  let current = record;
  for (let from = format; from < CATALOGUE_FORMAT; from++) {
    const step = UPGRADERS[from];
    if (!step || !isObj(current)) throw new Error(`No upgrade from catalogue format ${from}`);
    current = step(current);
  }
  return current;
}
